import { Question, TemplateField, TemplateValue, QuestionPart, Test } from '@shared/types/database'

// When this is sent over, a new modified child template gets saved
// to the database
export interface NewTemplatePayload {
  title: string
  questions: Question[]
  templateFields: Record<string, TemplateField>
  parentId: number
}

export interface TemplateSubmissionPayload {
  title?: string
  objective?: string
  decisionContext?: string
  draft?: boolean
  saveNewTemplate?: boolean
  renderedTemplate: Question[]
  templateFieldValues?: Record<string, TemplateValue> | null
  newTemplatePayload?: NewTemplatePayload
  testTemplateId?: number
}

export const humanReadableQuestionTypes = {
  'single-select': 'Single Select',
  'multi-select': 'Multi Select',
  rank: 'Rank',
  textarea: 'Text Response',
  number: 'Numeric',
  variant: 'Random Variant',
  rate: 'Rate and Explain',
  'rate-only': 'Rate',
  'matrix-likert': 'Matrix',
  description: 'Description',
  task: 'Task',
  verbal: 'Verbal'
}

export function questionTypeDisplayName (question: Question) {
  let displayName = humanReadableQuestionTypes[question.questionType]
  if (question.questionType === 'single-select' && question.explain) displayName += ' and Explain'
  return displayName
}

export function defaultFieldValue (field: TemplateField) {
  switch (field.type) {
    case 'list':
      return ['', '']
    case 'url':
    case 'asset':
      return
    default:
      return ''
  }
}

export function randomId () {
  return hex(Date.now() / 1000) +
    ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16))
}

function hex (value: number) {
  return Math.floor(value).toString(16)
}

export function generateTemplateSubmissionPayloadFromTest (test: Test): TemplateSubmissionPayload {
  const { title, testTemplateId, templateFieldValues, renderedTemplate } = test.payload
  if (!title || !testTemplateId || !templateFieldValues || !renderedTemplate) {
    console.error(
      'Test missing required template fields',
      { title, testTemplateId, templateFieldValues, renderedTemplate }
    )
  }
  return {
    title,
    testTemplateId: (testTemplateId as number),
    templateFieldValues,
    renderedTemplate: (renderedTemplate as Question[])
  }
}

export function getLabelParts (question: Question, templateFields: Record<string, TemplateField>): QuestionPart[] {
  // Taking the label and splitin the slots, inserting template fields in between
  // each element. Because a slot at the begging or end will insert an empty string
  // we then filter those pieces out
  return question.label
    .split('{{slot}}')
    .reduce((acc, curr, index, array) => {
      if (question.edited) {
        if (index === 0) {
          if (!('templateFields' in question)) return acc
          const inlineFieldId = question.templateFields.find((templateField) => templateField.placement === 'inline')
          if (inlineFieldId && inlineFieldId.id) {
            acc.push({
              type: 'field',
              field: {
                ...templateFields[inlineFieldId.id],
                id: inlineFieldId.id
              }
            })
          }
        }
      } else {
        const last = index === array.length - 1
        acc.push({
          type: 'text',
          text: curr
        })
        // We are inserting fields in between each element, so
        // we don't want one at the end
        if (!last) {
          if (!('templateFields' in question)) return acc
          const field: TemplateField = question.templateFields[index]
          if (!field.id) return acc
          acc.push({
            type: 'field',
            field: {
              ...templateFields[field.id],
              id: field.id
            }
          })
        }
      }
      return acc
    }, [] as QuestionPart[])
    .filter(part => part.text !== '')
}

export function getPlainTextLabel (question: Question, templateFields: Record<string, TemplateField>, templateFieldValues: Record<string, TemplateValue>): string {
  let questionPrompt = ''
  const questionParts = getLabelParts(question, templateFields)
  for (let i = 0; i < questionParts.length; i++) {
    const questionPart = questionParts[i]
    if (questionPart.text && questionPart.type === 'text') questionPrompt += questionPart.text
    else if (questionPart.field && questionPart.type === 'field') {
      const { id, placeholder } = questionPart.field
      if (id) {
        if (templateFieldValues[id].value === '') {
          questionPrompt += `[${placeholder}] `
        } else if (templateFieldValues[id].value) {
          questionPrompt += ` ${templateFieldValues[id].value} `
        }
      } else {
        console.log('The template field doesnt have an ID! This should not happen.')
      }
    }
  }
  return questionPrompt
}
